// SearchSelect — compact searchable multi-select used for every analytics filter
// dimension (campus, category, method, vendor, businessEx). Ported from the
// Westfert "CustomerSelect" so both apps filter the same way.
// Keeps the filter state shape (value = array of selected keys); onChange(nextArray).
// Closed: removable tags (or "All …") + a search input. Open: a portaled popover
// with quick actions, a searchable checklist (name + optional spend), and a footer.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Icon from "./Icon.jsx";
import { fmtRm } from "../lib/format.js";

export default function SearchSelect({ value, onChange, options = [], revenueByKey = {}, noun = { singular: "item", plural: "items" } }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rect, setRect] = useState(null);   // control position, for the portaled popover
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const popRef = useRef(null);

  const sel = value ?? [];
  const rev = (k) => revenueByKey[k] ?? 0;
  const hasRev = Object.keys(revenueByKey).length > 0;

  // Track the control's viewport position so the portaled popover can anchor to
  // it (and follow on scroll/resize). The popover is rendered into document.body
  // — NOT inside this component — so it escapes the page's `.rise` transform
  // stacking context and can never sit behind later sections or the mobile chrome.
  useEffect(() => {
    if (!open) return;
    const place = () => { if (wrapRef.current) setRect(wrapRef.current.getBoundingClientRect()); };
    place();
    window.addEventListener("scroll", place, true);   // capture → catches scrolls in any ancestor
    window.addEventListener("resize", place);
    return () => { window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); };
  }, [open]);

  // Close on outside-click — the popover lives in a portal, so check it too.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const toggle = (k) => onChange(sel.includes(k) ? sel.filter((x) => x !== k) : [...sel, k]);
  const remove = (k) => onChange(sel.filter((x) => x !== k));
  const top10 = () => onChange([...options].sort((a, b) => rev(b) - rev(a)).slice(0, 10));

  const ql = q.trim().toLowerCase();
  const filtered = ql ? options.filter((o) => o.toLowerCase().includes(ql)) : options;

  const renderName = (name) => {
    if (!ql) return name;
    const i = name.toLowerCase().indexOf(ql);
    if (i < 0) return name;
    return <>{name.slice(0, i)}<mark>{name.slice(i, i + ql.length)}</mark>{name.slice(i + ql.length)}</>;
  };

  return (
    <div className={`cust-select ${open ? "open" : ""}`} ref={wrapRef}>
      <div className="cust-control" onClick={() => { setOpen(true); inputRef.current?.focus(); }}>
        {sel.length === 0 ? (
          <span className="cust-all"><Icon name="globe" size={14} /> All {noun.plural}</span>
        ) : (
          sel.map((k) => (
            <span className="cust-tag" key={k}>
              <span className="t" title={k}>{k}</span>
              <button type="button" onClick={(e) => { e.stopPropagation(); remove(k); }} aria-label={`Remove ${k}`}>
                <Icon name="x" size={11} />
              </button>
            </span>
          ))
        )}
        <input ref={inputRef} className="cust-input" type="text" value={q}
          placeholder={sel.length ? "Add another…" : `Search ${noun.plural}…`}
          onChange={(e) => setQ(e.target.value)} onFocus={() => setOpen(true)} />
        <Icon name="search" size={15} className="cust-caret" />
      </div>

      {open && rect && createPortal((() => {
        const vh = window.innerHeight;
        const TABBAR = 72;                       // keep clear of the fixed mobile bottom nav
        const spaceBelow = vh - rect.bottom - TABBAR;
        const spaceAbove = rect.top - 12;
        const flipUp = spaceBelow < 280 && spaceAbove > spaceBelow;
        const style = {
          position: "fixed", left: rect.left, width: rect.width, right: "auto", zIndex: 80,
          display: "flex", flexDirection: "column",
          ...(flipUp
            ? { bottom: vh - rect.top + 6, top: "auto", maxHeight: Math.max(180, spaceAbove) }
            : { top: rect.bottom + 6, maxHeight: Math.max(180, spaceBelow) }),
        };
        return (
        <div className="cust-pop" ref={popRef} style={style}>
          <div className="cust-quick">
            <button type="button" onClick={() => onChange([])}><Icon name="globe" size={13} /> All {noun.plural}</button>
            {hasRev && <button type="button" onClick={top10}><Icon name="report" size={13} /> Top 10 by spend</button>}
            {sel.length > 0 && <button type="button" onClick={() => onChange([])}><Icon name="refresh" size={13} /> Clear ({sel.length})</button>}
          </div>
          <div className="cust-list">
            {filtered.length === 0 ? (
              <div className="cust-empty">No {noun.plural} match “{q.trim()}”.</div>
            ) : filtered.map((o) => {
              const on = sel.includes(o);
              return (
                <div className={`cust-opt ${on ? "on" : ""}`} key={o} onClick={() => toggle(o)}>
                  <span className="box">{on && <Icon name="check" size={11} />}</span>
                  <span className="nm">{renderName(o)}</span>
                  {hasRev && <span className="rv num">{fmtRm(rev(o))}</span>}
                </div>
              );
            })}
          </div>
          <div className="cust-foot">
            <span>{sel.length} selected · {filtered.length} listed</span>
            <button type="button" className="btn primary sm" onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
        );
      })(), document.body)}
    </div>
  );
}
